import { useEffect, useState } from 'react';
import * as Icons from '../icons';
import type { SearchResult, Criterion } from '../types';
import { titleCase, dateRange, durationMonths, fmtDuration, totalExperienceMonths, avgTenureMonths, currentRoleMonths } from '../util';
import { setProspectState, unsetProspectState } from '../api';
import type { SearchDoc } from '../api';

interface Props {
  search: SearchDoc;
  results: SearchResult[];
  criteria: Criterion[];
  totalCount: number;
  onBack: () => void;
}

export function ReviewView({ search, results, criteria, totalCount, onBack }: Props) {
  const [i, setI] = useState(0);
  // Track shortlist toggles made in this session so the button reflects state immediately.
  const [localShortlist, setLocalShortlist] = useState<Set<string>>(() => {
    const s = new Set<string>();
    for (const r of results) {
      if (r.contact.recruiterStates?.includes('shortlisted')) s.add(r.contact.id);
    }
    return s;
  });
  const r = results[i];
  if (!r) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[var(--surface)] p-10 text-[var(--text-muted)]">
        <p>No candidates to review.</p>
        <button
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm hover:bg-[var(--surface-muted)]"
          onClick={onBack}
        >
          ← Back to results
        </button>
      </div>
    );
  }
  const c = r.contact;
  const p = c.profileData;

  const isShortlisted = localShortlist.has(c.id);
  const toggleShortlist = async () => {
    if (isShortlisted) {
      setLocalShortlist(prev => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
      try {
        await unsetProspectState(search._id, c.id, 'shortlisted');
      } catch {
        setLocalShortlist(prev => new Set(prev).add(c.id));
      }
    } else {
      setLocalShortlist(prev => new Set(prev).add(c.id));
      try {
        await setProspectState(search._id, c.id, 'shortlisted', { snapshot: c });
      } catch {
        setLocalShortlist(prev => {
          const next = new Set(prev);
          next.delete(c.id);
          return next;
        });
      }
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setI(x => Math.min(results.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1));
      if (e.shiftKey && e.key.toLowerCase() === 's') {
        toggleShortlist();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search._id, c.id, results.length, isShortlisted]);

  return (
    <div className="flex flex-1 flex-col bg-[var(--surface)]">
      <div className="flex items-center gap-4 border-b border-[var(--border-default)] px-6 py-3">
        <button
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]"
          onClick={onBack}
        >
          <Icons.ArrowLeft />
        </button>
        <h1 className="m-0 text-lg font-medium">Review Profiles</h1>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1fr_1fr_320px]">
        {/* Left — profile */}
        <div className="overflow-y-auto border-r border-[var(--border-default)] p-6">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="m-0 text-[22px]">{titleCase(p.full_name)}</h2>
            <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--accent-subtle)] px-2 py-0.5 text-[13px] text-[var(--accent-strong)]">
              Full profile
            </span>
            <span className="flex-1" />
            {p.linkedin_url ? (
              <span
                className="inline-flex items-center justify-center rounded-md text-white"
                style={{ width: 22, height: 22, background: '#0a66c2' }}
              >
                <Icons.LinkedIn style={{ width: 14, height: 14 }} />
              </span>
            ) : null}
          </div>
          <div className="mb-2.5 text-sm text-[var(--text-muted)]">{titleCase(p.location_name)}</div>
          <div className="mb-4 flex flex-wrap gap-2">
            {p.job_company_name ? (
              <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--surface-tertiary)] px-2 py-1 text-[13px]">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[9px] font-bold text-[var(--text-muted)]">
                  {titleCase(p.job_company_name).slice(0, 1)}
                </span>
                {titleCase(p.job_company_name)}
              </span>
            ) : null}
            {p.education?.[0]?.school?.name ? (
              <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--surface-tertiary)] px-2 py-1 text-[13px]">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[9px] font-bold text-[var(--text-muted)]">
                  {titleCase(p.education[0].school.name).slice(0, 1)}
                </span>
                {titleCase(p.education[0].school.name).split(' ').slice(0, 2).join(' ')}
              </span>
            ) : null}
          </div>

          <div className="mb-5 flex gap-6 border-b border-[var(--border-default)]">
            <button className="border-b-2 border-[var(--accent)] pb-3 text-sm font-medium text-[var(--text-primary)]">
              Experience
            </button>
            <button className="border-b-2 border-transparent pb-3 text-sm text-[var(--text-muted)]">
              Education
            </button>
            <button className="border-b-2 border-transparent pb-3 text-sm text-[var(--text-muted)]">
              Skills
            </button>
          </div>

          <h3 className="mt-2 flex items-center gap-2 text-[15px] font-semibold">
            Experience
            <span className="text-[13px] font-normal text-[var(--text-muted)]">
              · {fmtDuration(totalExperienceMonths(c))} total · {fmtDuration(avgTenureMonths(c))} average tenure
            </span>
          </h3>
          <div className="my-3.5 flex gap-3">
            <StatCard label="Avg tenure" value={fmtDuration(avgTenureMonths(c))} />
            <StatCard label="Current tenure" value={fmtDuration(currentRoleMonths(c))} />
            <StatCard label="Total experience" value={fmtDuration(totalExperienceMonths(c))} />
          </div>
          {(p.experience || []).map((x, j) => {
            const months = durationMonths(x.start_date, x.end_date);
            return (
              <div key={j} className="flex gap-3 border-b border-[var(--border-muted)] py-3.5">
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]">
                  {titleCase(x.company.name).slice(0, 1)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{titleCase(x.title?.name)}</div>
                  <div className="text-sm">{titleCase(x.company?.name)}</div>
                  <div className="mt-0.5 text-[13px] text-[var(--text-muted)]">
                    {dateRange(x.start_date, x.end_date)} · {fmtDuration(months)}
                  </div>
                  {x.summary ? <div className="mt-1.5 text-[13px]">{x.summary}</div> : null}
                  <a className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[13px] text-[var(--text-link)] hover:underline">
                    <Icons.Bolt style={{ width: 12, height: 12 }} />
                    Unlock Compensation Estimate
                  </a>
                </div>
              </div>
            );
          })}

          <h3 className="mt-6 text-[15px] font-semibold">Education</h3>
          {(p.education || []).map((e, k) => (
            <div key={k} className="flex gap-3 border-b border-[var(--border-muted)] py-3.5">
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]">
                {titleCase(e.school?.name).slice(0, 1)}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{titleCase(e.school?.name)}</div>
                {e.degrees?.length ? (
                  <div className="text-sm">
                    {titleCase(e.degrees[0])}
                    {e.majors?.length ? ', ' + titleCase(e.majors[0]) : ''}
                  </div>
                ) : null}
                <div className="mt-0.5 text-[13px] text-[var(--text-muted)]">
                  {dateRange(e.start_date, e.end_date)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Middle — match reasoning */}
        <div className="overflow-y-auto border-r border-[var(--border-default)] p-6">
          <h3 className="mt-0 text-[15px] font-semibold">Why we matched this profile</h3>
          <div className="mb-3 inline-flex items-center gap-1 rounded-sm bg-[var(--bg-success)] px-2.5 py-1 text-[13px] text-[var(--fg-success)]">
            <Icons.ThumbUp style={{ width: 14, height: 14 }} /> Good Match
          </div>
          {r.reasons.map((x, k) => (
            <div key={k} className="mb-3.5 py-1.5">
              <div>
                <b className="font-medium">{x.rationale}</b>
                <span className="ml-1.5 inline-block min-w-[18px] rounded-sm bg-[var(--accent-muted)] px-1.5 text-center text-[11px] font-semibold text-[var(--accent-strong)]">
                  {x.criterionIndex}
                </span>
              </div>
            </div>
          ))}
          {criteria.map((cr, k) => {
            const reason = r.reasons.find(rr => rr.criterionIndex === k + 1);
            if (reason) return null;
            return (
              <div key={k} className="mb-3.5 py-1.5">
                <div className="text-[var(--text-muted)]">
                  {cr.text}
                  <span className="ml-1.5 inline-block min-w-[18px] rounded-sm bg-[var(--accent-muted)] px-1.5 text-center text-[11px] font-semibold text-[var(--accent-strong)]">
                    {k + 1}
                  </span>
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-muted)]">No clear evidence of this criterion</div>
              </div>
            );
          })}
        </div>

        {/* Right — controls */}
        <div className="overflow-y-auto p-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                <button className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]">
                  <Icons.ChevronLeft />
                  <Icons.ChevronLeft style={{ marginLeft: -10 }} />
                </button>
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]"
                  onClick={() => setI(Math.max(0, i - 1))}
                >
                  <Icons.ChevronLeft />
                </button>
              </div>
              <div className="text-sm">
                Profile {i + 1}/{totalCount}
              </div>
              <div className="flex gap-1">
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]"
                  onClick={() => setI(Math.min(results.length - 1, i + 1))}
                >
                  <Icons.ChevronRight />
                </button>
                <button className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]">
                  <Icons.ChevronRight />
                  <Icons.ChevronRight style={{ marginLeft: -10 }} />
                </button>
              </div>
            </div>
            <button
              className={
                'inline-flex items-center justify-between gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ' +
                (isShortlisted
                  ? 'border-[var(--accent)] bg-[var(--accent-muted)] font-medium text-[var(--accent-strong)] hover:bg-[var(--accent-subtle)]'
                  : 'border-[var(--border-default)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]')
              }
              onClick={toggleShortlist}
            >
              <span className="inline-flex items-center gap-1.5">
                <Icons.Bookmark style={{ width: 14, height: 14 }} />{' '}
                {isShortlisted ? 'Shortlisted' : 'Add to Shortlist'}
              </span>
              <span className="ml-2 inline-flex items-center rounded-sm bg-[var(--surface-tertiary)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--text-muted)]">
                ⇧ S
              </span>
            </button>
            <a
              className="cursor-pointer text-center text-sm text-[var(--text-muted)] hover:underline"
              onClick={() => setI(Math.min(results.length - 1, i + 1))}
            >
              Skip
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 rounded-md border border-[var(--border-muted)] bg-[var(--surface-muted)] px-3 py-2.5">
      <div className="text-[12px] text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-sm font-semibold">{value}</div>
    </div>
  );
}
